import React, { useContext } from 'react'
import { FormattedMessage } from 'react-intl'

import RemoveIcon from 'assets/icons/remove-icon'
import AssessIcon from 'assets/icons/assess-icon'
import Button from 'components/Button'
import { congregationOptions } from 'constants/congregationOptions'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import PulsingNotification from 'features/PulsingNotification'
import { SiteContext } from 'providers/site'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import Tooltip from 'components/Tooltip'
import CalendarIcon from 'assets/icons/calendar-icon'
import ConfirmIcon from 'assets/icons/confirm-icon'
import RejectIcon from 'assets/icons/reject-icon'
import DropdownIndicatorIconClosed from 'assets/icons/dropdown-indicator-closed24px'
import DropdownIndicatorIconOpen from 'assets/icons/dropdown-indicator-opened24px'
import ReassessIcon from 'assets/icons/reassess-icon'
import { reassessmentTriggerReasons } from 'constants/reassessmentTriggerReasons'

import {
  CongregationHeaderCalendarDateText, CongregationHeaderCalendarDivider,
  CongregationHeaderCalendarIconWrapper,
  CongregationHeaderDotIcon,
  CongregationHeaderHalfSide,
  CongregationHeaderRemoveIconWrapper,
  CongregationHeaderTitle,
  CongregationHeaderWrapper,
  CongregationsHeaderLastReassessmentCalendarWrapper,
  CongregationValidationIcon,
  CongregationValidationIconWrapper,
  CongregationConfirmDeclineIcon,
  CongregationToggleWrapper
} from './style'


const tooltipXOffset = 0
const tooltipYOffset = -16
const dateOptions = {
  year: "numeric",
  month: "short",
  weekday: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
  hour12: true // This is the line of code we care about here
  /*
      false: displays 24hs format for time
      true: displays 12, AM and PM format
  */
};

const CongregationHeader = ({ isOpen, item, toggleAccordion, lastItem, openRemoveModal, site, newTriggers = false, oldTriggers = false }) => {
  const { siteProposalFieldValidation, isEditable } = useContext(SiteContext)

  const dateToShow = item.updatedAt ? new Date(item.updatedAt) : new Date()
  const name = congregationOptions.find((element) => {
    return element.value === item.CongregationGroupId
  })

  const removeItem = () => {
    openRemoveModal({ ...item, name: name ? name.label : 'New Congregation' })
  }

  const validationCongregation = siteProposalFieldValidation?.congregations?.[item.id] || defaultSiteProposalValidationResults.congregations

  const fieldsToCompleteNumber = Object.values(validationCongregation).filter((element) => {
    return element === false
  })

  const formattedRejectIconTooltipHint = (item) => {
    return item.trigger_reason_id?.map((element) => {
      return reassessmentTriggerReasons.find((elementId) => {
        return elementId.id === Number(element)
      })?.name
    }).filter(Boolean) || []
  }

  const qualifies = item.trigger_reason_id == undefined ? true : item.trigger_reason_id?.some((element) => {
    return Number(element) === 1
  })

  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID) || !qualifies

  return (
    <>
      <CongregationHeaderWrapper borderBottom={isOpen || lastItem} marginBottom={isOpen} data-testId='congregation-header'>
        <CongregationHeaderHalfSide onClick={toggleAccordion}>
          <CongregationHeaderDotIcon isRejected={!qualifies} />
          <CongregationHeaderTitle isRejected={!qualifies} isReassess={item.lastReassessmentDate}>
            <FormattedMessage id={name ? name.label : 'newCongregationTitle'} />
          </CongregationHeaderTitle>
        </CongregationHeaderHalfSide>
        <CongregationHeaderHalfSide>
          {site.reassessmentFlag ? (
            <>
              {dateToShow !== '' && (
                <Tooltip
                  style={{ zIndex: 1000 }}
                  placement='top'
                  trigger={['hover']}
                  align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                  overlay={{ label: 'lastTimeReassessed' }}
                >
                  <CongregationsHeaderLastReassessmentCalendarWrapper>
                    <CongregationHeaderCalendarIconWrapper>
                      <CalendarIcon />
                    </CongregationHeaderCalendarIconWrapper>
                    <CongregationHeaderCalendarDateText>
                      {dateToShow.toLocaleDateString('en-US', dateOptions)}
                    </CongregationHeaderCalendarDateText>
                    <CongregationHeaderCalendarDivider />
                  </CongregationsHeaderLastReassessmentCalendarWrapper>
                </Tooltip>
              )}
              <CongregationConfirmDeclineIcon type={qualifies ? 'confirmed' : 'rejected'}>
                {qualifies ? (
                  <Tooltip
                    style={{ zIndex: 1000 }}
                    placement='top'
                    trigger={['hover']}
                    align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                    overlay={{ label: 'qualifies' }}
                  >
                    <div>
                      <ConfirmIcon />
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip
                    style={{ zIndex: 1000 }}
                    placement='top'
                    trigger={['hover']}
                    align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                    overlay={{ label: 'nonQualifying', infoTooltip: formattedRejectIconTooltipHint(item).join(', ') }}
                  >
                    <div>
                      <RejectIcon />
                    </div>
                  </Tooltip>
                )}
              </CongregationConfirmDeclineIcon>
              {site.SiteStatusId === SITE_STATUS_DRAFT_ID ? (
                <>
                  <Button
                    isDisabled={isOpen}
                    type={isOpen ? 'defaultDisabled' : ' '}
                    height='32px'
                    marginTop='0'
                    onClick={toggleAccordion}>
                    <ReassessIcon />
                    <FormattedMessage id={oldTriggers ? 'reassess' : newTriggers ? 'assess' : 'reassess'} />
                  </Button>
                </>
              ) : (
                <>
                  <CongregationToggleWrapper isActive={isOpen} onClick={toggleAccordion}>
                    <FormattedMessage id='expand' />
                    {isOpen ? <DropdownIndicatorIconOpen /> : <DropdownIndicatorIconClosed />}
                  </CongregationToggleWrapper>
                </>
              )}
              {(!item.lastReassessmentDate && site.SiteStatusId === SITE_STATUS_DRAFT_ID && !disabled) && (
                <>
                  <CongregationHeaderRemoveIconWrapper
                    delete={true}
                    data-testid='remove-button'
                    onClick={(e) => {
                      e.stopPropagation()
                      openRemoveModal(item)
                    }}
                  >
                    <RemoveIcon />
                  </CongregationHeaderRemoveIconWrapper>
                </>
              )}
              {fieldsToCompleteNumber.length > 0 && (
                <CongregationValidationIconWrapper>
                  <CongregationValidationIcon>
                    <PulsingNotification number={fieldsToCompleteNumber.length} />
                  </CongregationValidationIcon>
                </CongregationValidationIconWrapper>
              )}
            </>
          ) : (
            <>
              <>
                {site.SiteStatusId === SITE_STATUS_DRAFT_ID ? (
                  <Button
                    height='32px'
                    onClick={toggleAccordion}
                    marginTop='0'
                    isDisabled={isOpen}
                    type={isOpen ? 'defaultDisabled' : ''}
                  >
                    <AssessIcon />
                    <FormattedMessage id={oldTriggers ? 'reassess' : newTriggers ? 'assess' : 'reassess'} />
                  </Button>
                ) : (
                  <>
                    {dateToShow !== '' && (
                      <Tooltip
                        style={{ zIndex: 1000 }}
                        placement='top'
                        trigger={['hover']}
                        align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                        overlay={{ label: 'lastTimeReassessed' }}
                      >
                        <CongregationsHeaderLastReassessmentCalendarWrapper>
                          <CongregationHeaderCalendarIconWrapper>
                            <CalendarIcon />
                          </CongregationHeaderCalendarIconWrapper>
                          <CongregationHeaderCalendarDateText>
                            {dateToShow.toLocaleDateString('en-US', dateOptions)}
                          </CongregationHeaderCalendarDateText>
                          <CongregationHeaderCalendarDivider />
                        </CongregationsHeaderLastReassessmentCalendarWrapper>
                      </Tooltip>
                    )}
                    <CongregationConfirmDeclineIcon type={qualifies ? 'confirmed' : 'rejected'}>
                      {qualifies ? (
                        <Tooltip
                          style={{ zIndex: 1000 }}
                          placement='top'
                          trigger={['hover']}
                          align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                          overlay={{ label: 'qualifies' }}
                        >
                          <div>
                            <ConfirmIcon />
                          </div>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          style={{ zIndex: 1000 }}
                          placement='top'
                          trigger={['hover']}
                          align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                          overlay={{ label: 'nonQualifying', infoTooltip: formattedRejectIconTooltipHint(item).join(', ') }}
                        >
                          <div>
                            <RejectIcon />
                          </div>
                        </Tooltip>
                      )}
                    </CongregationConfirmDeclineIcon>
                    <CongregationToggleWrapper isActive={isOpen} onClick={toggleAccordion}>
                      <FormattedMessage id='expand' />
                      {isOpen ? <DropdownIndicatorIconOpen /> : <DropdownIndicatorIconClosed />}
                    </CongregationToggleWrapper>
                  </>
                )}
              </>
              {site.SiteStatusId === SITE_STATUS_DRAFT_ID && !disabled && (
                <>
                  <CongregationHeaderRemoveIconWrapper
                    data-testid='remove-button'
                    onClick={(e) => {
                      e.stopPropagation()
                      openRemoveModal(item)
                    }}
                  >
                    <RemoveIcon />
                  </CongregationHeaderRemoveIconWrapper>
                </>
              )}
              {fieldsToCompleteNumber.length > 0 && (
                <CongregationValidationIconWrapper>
                  <CongregationValidationIcon>
                    <PulsingNotification number={fieldsToCompleteNumber.length} />
                  </CongregationValidationIcon>
                </CongregationValidationIconWrapper>
              )}
            </>
          )}
        </CongregationHeaderHalfSide>
      </CongregationHeaderWrapper>
    </>
  )
}

export default CongregationHeader
